import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const SecuritySettings = () => {
  const [securitySettings, setSecuritySettings] = useState({
    twoFactorEnabled: true,
    sessionTimeout: 30,
    loginNotifications: true,
    ipWhitelist: false,
    apiKeyAccess: true,
    autoLogout: true,
    passwordExpiry: 90,
    deviceTrust: true,
    encryptLocalData: true,
    biometricAuth: false
  });

  const [apiKeys, setApiKeys] = useState([
    {
      id: 1,
      name: 'Trading Bot API',
      key: 'pts_live_ak_1a2b3c4d5e6f7g8h9i0j',
      permissions: ['read', 'trade'],
      created: '2024-01-15',
      lastUsed: '2024-01-20',
      status: 'active'
    },
    {
      id: 2,
      name: 'Portfolio Tracker',
      key: 'pts_live_ak_9z8y7x6w5v4u3t2s1r0q',
      permissions: ['read'],
      created: '2024-01-10',
      lastUsed: '2024-01-19',
      status: 'active'
    }
  ]);

  const [sessions, setSessions] = useState([
    {
      id: 1,
      device: 'Chrome on Windows',
      location: 'New York, NY',
      ipAddress: '192.168.1.100',
      lastActive: '2024-01-20 14:30:00',
      current: true
    },
    {
      id: 2,
      device: 'Safari on iPhone',
      location: 'New York, NY',
      ipAddress: '192.168.1.101',
      lastActive: '2024-01-20 12:15:00',
      current: false
    }
  ]);

  const [newApiKey, setNewApiKey] = useState({
    name: '',
    permissions: []
  });

  const [showCreateApiKey, setShowCreateApiKey] = useState(false);
  const [show2FASetup, setShow2FASetup] = useState(false);

  const sessionTimeoutOptions = [
    { value: 15, label: '15 minutes' },
    { value: 30, label: '30 minutes' },
    { value: 60, label: '1 hour' },
    { value: 120, label: '2 hours' },
    { value: 240, label: '4 hours' },
    { value: 480, label: '8 hours' },
    { value: 0, label: 'Never' }
  ];

  const passwordExpiryOptions = [
    { value: 30, label: '30 days' },
    { value: 60, label: '60 days' },
    { value: 90, label: '90 days' },
    { value: 180, label: '180 days' },
    { value: 365, label: '1 year' },
    { value: 0, label: 'Never' }
  ];

  const permissionOptions = [
    { value: 'read', label: 'Read Access' },
    { value: 'trade', label: 'Trading Access' },
    { value: 'withdraw', label: 'Withdrawal Access' },
    { value: 'admin', label: 'Admin Access' }
  ];

  const handleSettingChange = (setting, value) => {
    setSecuritySettings(prev => ({
      ...prev,
      [setting]: value
    }));
  };

  const handleCheckboxChange = (setting, checked) => {
    setSecuritySettings(prev => ({
      ...prev,
      [setting]: checked
    }));
  };

  const handleCreateApiKey = () => {
    if (newApiKey?.name && newApiKey?.permissions?.length > 0) {
      const apiKey = {
        id: apiKeys?.length + 1,
        name: newApiKey?.name,
        key: `pts_live_ak_${Math.random()?.toString(36)?.substring(2, 22)}`,
        permissions: newApiKey?.permissions,
        created: new Date()?.toISOString()?.split('T')?.[0],
        lastUsed: 'Never',
        status: 'active'
      };
      setApiKeys(prev => [...prev, apiKey]);
      setNewApiKey({ name: '', permissions: [] });
      setShowCreateApiKey(false);
    }
  };

  const handleRevokeApiKey = (keyId) => {
    setApiKeys(prev => prev?.filter(key => key?.id !== keyId));
  };

  const handleTerminateSession = (sessionId) => {
    setSessions(prev => prev?.filter(session => session?.id !== sessionId));
  };

  const handleEnable2FA = () => {
    setSecuritySettings(prev => ({ ...prev, twoFactorEnabled: true }));
    setShow2FASetup(false);
  };

  const handleDisable2FA = () => {
    setSecuritySettings(prev => ({ ...prev, twoFactorEnabled: false }));
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Security Settings</h3>
          <p className="text-sm text-muted-foreground">Manage your account security and access controls</p>
        </div>
        <div className="flex items-center space-x-2">
          <div className="flex items-center space-x-2 px-3 py-1 bg-success/10 text-success rounded-md">
            <Icon name="Shield" size={16} />
            <span className="text-sm font-medium">Secure</span>
          </div>
        </div>
      </div>
      {/* Two-Factor Authentication */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h4 className="text-md font-medium text-foreground">Two-Factor Authentication</h4>
            <p className="text-sm text-muted-foreground">Add an extra layer of security to your account</p>
          </div>
          <div className="flex items-center space-x-2">
            {securitySettings?.twoFactorEnabled ? (
              <div className="flex items-center space-x-2 text-success">
                <Icon name="CheckCircle" size={16} />
                <span className="text-sm font-medium">Enabled</span>
              </div>
            ) : (
              <div className="flex items-center space-x-2 text-warning">
                <Icon name="AlertTriangle" size={16} />
                <span className="text-sm font-medium">Disabled</span>
              </div>
            )}
          </div>
        </div>

        {securitySettings?.twoFactorEnabled ? (
          <div className="space-y-4">
            <div className="bg-success/5 border border-success/20 rounded-md p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="Smartphone" size={16} className="text-success" />
                <span className="text-sm font-medium text-foreground">Authenticator App Connected</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Your account is protected with Google Authenticator. Backup codes: 8 remaining
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <Button variant="outline" size="sm" iconName="Download" iconPosition="left">
                Download Backup Codes
              </Button>
              <Button variant="destructive" size="sm" onClick={handleDisable2FA}>
                Disable 2FA
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="bg-warning/5 border border-warning/20 rounded-md p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="AlertTriangle" size={16} className="text-warning" />
                <span className="text-sm font-medium text-foreground">Two-Factor Authentication Disabled</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Your account is not protected by 2FA. Enable it now for better security.
              </p>
            </div>
            <Button variant="default" onClick={() => setShow2FASetup(true)} iconName="Shield" iconPosition="left">
              Enable Two-Factor Authentication
            </Button>
          </div>
        )}
      </div>
      {/* Session Management */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h4 className="text-md font-medium text-foreground mb-4">Session Management</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <Select
            label="Session Timeout"
            description="Automatically log out after inactivity"
            options={sessionTimeoutOptions}
            value={securitySettings?.sessionTimeout}
            onChange={(value) => handleSettingChange('sessionTimeout', value)}
          />
          <div className="space-y-3">
            <label className="text-sm font-medium text-foreground">Session Options</label>
            <div className="space-y-2">
              <Checkbox
                label="Auto Logout"
                description="Automatically log out when session expires"
                checked={securitySettings?.autoLogout}
                onChange={(e) => handleCheckboxChange('autoLogout', e?.target?.checked)}
              />
              <Checkbox
                label="Login Notifications"
                description="Get notified of new login attempts"
                checked={securitySettings?.loginNotifications}
                onChange={(e) => handleCheckboxChange('loginNotifications', e?.target?.checked)}
              />
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h5 className="text-sm font-medium text-foreground">Active Sessions</h5>
            <Button variant="outline" size="sm" iconName="RefreshCw" iconPosition="left">
              Refresh
            </Button>
          </div>
          <div className="space-y-2">
            {sessions?.map((session) => (
              <div key={session?.id} className="flex items-center justify-between p-3 bg-muted rounded-md">
                <div className="flex items-center space-x-3">
                  <Icon name={session?.current ? "Monitor" : "Smartphone"} size={16} className="text-muted-foreground" />
                  <div>
                    <div className="text-sm font-medium text-foreground">
                      {session?.device}
                      {session?.current && <span className="ml-2 text-xs text-success">(Current)</span>}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {session?.location} • {session?.ipAddress} • Last active: {session?.lastActive}
                    </div>
                  </div>
                </div>
                {!session?.current && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleTerminateSession(session?.id)}
                    iconName="X"
                  >
                    Terminate
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* API Key Management */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h4 className="text-md font-medium text-foreground">API Key Management</h4>
            <p className="text-sm text-muted-foreground">Manage API keys for third-party applications</p>
          </div>
          <Button
            variant="default"
            size="sm"
            onClick={() => setShowCreateApiKey(true)}
            iconName="Plus"
            iconPosition="left"
          >
            Create API Key
          </Button>
        </div>

        {showCreateApiKey && (
          <div className="bg-muted rounded-md p-4 mb-4">
            <h5 className="text-sm font-medium text-foreground mb-3">Create New API Key</h5>
            <div className="space-y-3">
              <Input
                label="API Key Name"
                placeholder="Enter a descriptive name"
                value={newApiKey?.name}
                onChange={(e) => setNewApiKey(prev => ({ ...prev, name: e?.target?.value }))}
              />
              <Select
                label="Permissions"
                description="Select what this API key can access"
                options={permissionOptions}
                multiple
                value={newApiKey?.permissions}
                onChange={(permissions) => setNewApiKey(prev => ({ ...prev, permissions }))}
              />
              <div className="flex items-center space-x-2">
                <Button variant="default" onClick={handleCreateApiKey} size="sm">
                  Create Key
                </Button>
                <Button variant="outline" onClick={() => setShowCreateApiKey(false)} size="sm">
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-3">
          {apiKeys?.map((apiKey) => (
            <div key={apiKey?.id} className="flex items-center justify-between p-3 bg-muted rounded-md">
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-1">
                  <span className="text-sm font-medium text-foreground">{apiKey?.name}</span>
                  <div className="flex items-center space-x-1">
                    {apiKey?.permissions?.map((permission) => (
                      <span key={permission} className="px-2 py-1 bg-primary/10 text-primary text-xs rounded">
                        {permission}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="text-xs text-muted-foreground font-mono">
                  {apiKey?.key}
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  Created: {apiKey?.created} • Last used: {apiKey?.lastUsed}
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Button variant="outline" size="sm" iconName="Copy">
                  Copy
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => handleRevokeApiKey(apiKey?.id)}
                  iconName="Trash2"
                >
                  Revoke
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Advanced Security */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h4 className="text-md font-medium text-foreground mb-4">Advanced Security</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <Select
              label="Password Expiry"
              description="Force password change after specified period"
              options={passwordExpiryOptions}
              value={securitySettings?.passwordExpiry}
              onChange={(value) => handleSettingChange('passwordExpiry', value)}
            />
            
            <div className="space-y-3">
              <label className="text-sm font-medium text-foreground">Security Features</label>
              <div className="space-y-2">
                <Checkbox
                  label="IP Whitelist"
                  description="Only allow access from specific IP addresses"
                  checked={securitySettings?.ipWhitelist}
                  onChange={(e) => handleCheckboxChange('ipWhitelist', e?.target?.checked)}
                />
                <Checkbox
                  label="Device Trust"
                  description="Remember trusted devices for 30 days"
                  checked={securitySettings?.deviceTrust}
                  onChange={(e) => handleCheckboxChange('deviceTrust', e?.target?.checked)}
                />
              </div>
            </div>
          </div>
          
          <div className="space-y-4">
            <div className="space-y-3">
              <label className="text-sm font-medium text-foreground">Data Protection</label>
              <div className="space-y-2">
                <Checkbox
                  label="Encrypt Local Data"
                  description="Encrypt sensitive data stored locally"
                  checked={securitySettings?.encryptLocalData}
                  onChange={(e) => handleCheckboxChange('encryptLocalData', e?.target?.checked)}
                />
                <Checkbox
                  label="Biometric Authentication"
                  description="Use fingerprint or face recognition (if supported)"
                  checked={securitySettings?.biometricAuth}
                  onChange={(e) => handleCheckboxChange('biometricAuth', e?.target?.checked)}
                />
              </div>
            </div>
            
            <div className="bg-warning/5 border border-warning/20 rounded-md p-3">
              <div className="flex items-center space-x-2 mb-1">
                <Icon name="AlertTriangle" size={14} className="text-warning" />
                <span className="text-xs font-medium text-foreground">Security Recommendation</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Enable all security features for maximum protection of your trading account.
              </p>
            </div>
          </div>
        </div>
      </div>
      {/* 2FA Setup Modal */}
      {show2FASetup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-card border border-border rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-semibold text-foreground">Setup Two-Factor Authentication</h4>
              <Button variant="ghost" size="sm" onClick={() => setShow2FASetup(false)}>
                <Icon name="X" size={16} />
              </Button>
            </div>
            
            <div className="space-y-4">
              <div className="text-center">
                <div className="w-32 h-32 bg-muted rounded-lg mx-auto mb-4 flex items-center justify-center">
                  <Icon name="QrCode" size={48} className="text-muted-foreground" />
                </div>
                <p className="text-sm text-muted-foreground">
                  Scan this QR code with your authenticator app
                </p>
              </div>
              
              <div className="bg-muted rounded-md p-3">
                <p className="text-xs text-muted-foreground mb-1">Manual entry key:</p>
                <p className="text-sm font-mono text-foreground">JBSWY3DPEHPK3PXP</p>
              </div>
              
              <Input
                label="Verification Code"
                placeholder="Enter 6-digit code from your app"
                description="Enter the code from your authenticator app to verify setup"
              />
              
              <div className="flex items-center space-x-2">
                <Button variant="default" onClick={handleEnable2FA} className="flex-1">
                  Enable 2FA
                </Button>
                <Button variant="outline" onClick={() => setShow2FASetup(false)} className="flex-1">
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Save Actions */}
      <div className="flex items-center justify-end space-x-4 pt-4 border-t border-border">
        <Button variant="outline">
          Reset to Defaults
        </Button>
        <Button variant="default" iconName="Save" iconPosition="left">
          Save Security Settings
        </Button>
      </div>
    </div>
  );
};

export default SecuritySettings;